import { isNil } from 'lodash';

import { MAX_OVERHANG_INCHES, MAX_OVERHANG_CM } from 'config/calculator';
import { LayerPatternType, UnitDimension } from 'enums/calculator';
import { LayerPatternItem, LoadDimensions } from 'interfaces/calculator';
import PatternId from 'enums/patterns';
import { convertTo } from './numbers';

export interface CalculatorOptions {
  packageDimensionType: UnitDimension;
  packageLength: number | null;
  packageWidth: number | null;
  palletLength?: number | null;
  palletUnitType: UnitDimension;
  palletWidth?: number | null;
  unitsPerLayer?: number | null;
  alignmentType?: LayerPatternType;
  horizontalOverlap: number;
  verticalOverlap: number;
}

export interface Dimensions {
  width: number;
  length: number;
  palletMaxWidth: number;
  palletMaxLength: number;
  widthOverhang: number;
  lengthOverhang: number;
  horizontalOverlap: number;
  verticalOverlap: number;
}

export interface OverlapValues {
  horizontal: number,
  vertical: number,
  allowed: boolean,
}

export interface Grid {
  cols: number;
  rows: number;
}

export const calculateGridSize = (
  dimensions: Dimensions,
  defaultVisibility: boolean = true,
) => {
  let newMaxWidth = dimensions.palletMaxWidth;
  let newMaxLength = dimensions.palletMaxLength;

  // For option A where palletWidth goes with package width.
  if (dimensions.width <= dimensions.widthOverhang) {
    newMaxWidth = dimensions.palletMaxWidth - 2 * dimensions.width;
  }
  if (dimensions.length <= dimensions.lengthOverhang) {
    newMaxLength = dimensions.palletMaxLength - 2 * dimensions.length;
  }
  const colsA = Math.floor(newMaxWidth / dimensions.width);
  const rowsA = Math.floor(newMaxLength / dimensions.length);
  const productsA = colsA * rowsA;

  // For option B where palletWidth goes with package length.
  if (dimensions.length <= dimensions.widthOverhang) {
    newMaxWidth = dimensions.palletMaxWidth - 2 * dimensions.length;
  }
  if (dimensions.width <= dimensions.lengthOverhang) {
    newMaxLength = dimensions.palletMaxLength - 2 * dimensions.width;
  }
  const colsB = Math.floor(newMaxWidth / dimensions.length);
  const rowsB = Math.floor(newMaxLength / dimensions.width);
  const productsB = colsB * rowsB;

  let numberProducts;
  let cols;
  let rows;
  let loadWidth;
  let loadLength;
  let rotatedPackage = false;
  if (productsA > productsB) {
    numberProducts = productsA;
    cols = colsA;
    rows = rowsA;
    loadWidth = cols * dimensions.width;
    loadLength = rows * dimensions.length;
  } else {
    numberProducts = productsB;
    cols = colsB;
    rows = rowsB;
    loadWidth = cols * dimensions.length;
    loadLength = rows * dimensions.width;
    rotatedPackage = true;
  }

  const load: LoadDimensions = {
    width: loadWidth,
    length: loadLength,
    isVisible: defaultVisibility,
    numberProducts,
    rotatedPackage,
    cols,
    rows,
  };
  return load;
};

const loadFitInPallet = (width: number, length: number, pallet: Dimensions) => {
  if (width < length && pallet.palletMaxWidth < pallet.palletMaxLength) {
    return width <= pallet.palletMaxWidth
      && length <= pallet.palletMaxLength;
  }
  return length <= pallet.palletMaxWidth
    && width <= pallet.palletMaxLength;
};

const calculateGrid = (
  rows: number,
  cols: number,
  dimensions: Dimensions,
  defaultVisibility: boolean = true,
) => {
  const width = dimensions.width * cols;
  const length = dimensions.length * rows;
  const isVisible = defaultVisibility && loadFitInPallet(width, length, dimensions);

  const load: LoadDimensions = {
    width,
    length,
    isVisible,
  };
  return load;
};

// Grid1 corresponds to cols and rows when the bigger side is placed horizontally.
// Grid2 corresponds to cols and rows when the smaller side is placed vertically.
const calculateComposedGrid = (
  grid1: Grid,
  grid2: Grid,
  dimensions: Dimensions,
  overlap: OverlapValues,
  defaultVisibility: boolean = true,
) => {
  const width = dimensions.length * grid1.cols + dimensions.width * grid2.cols - overlap.horizontal;
  const lengthA = dimensions.width * grid1.rows;
  const lengthB = dimensions.length * grid2.rows - ((grid2.rows - 1) * overlap.vertical);
  const length = lengthA > lengthB ? lengthA : lengthB;
  const isVisible = loadFitInPallet(width, length, dimensions);
  let rotate = false;

  if (!isVisible) {
    rotate = length <= dimensions.palletMaxWidth
      && width <= dimensions.palletMaxLength;
  }

  const load: LoadDimensions = {
    width,
    length,
    isVisible: defaultVisibility && (isVisible || rotate),
  };
  return load;
};

const calculate4A = (dimensions: Dimensions, overlap: OverlapValues) => {
  let load: LoadDimensions;

  if (overlap.allowed) {
    const width = dimensions.length + dimensions.width - overlap.horizontal;
    const length = dimensions.length + dimensions.width - overlap.vertical;
    const isVisible = loadFitInPallet(width, length, dimensions);

    load = {
      width,
      length,
      isVisible,
    };
  } else {
    const side = dimensions.length + dimensions.width;
    const isVisible = side <= dimensions.palletMaxLength && side <= dimensions.palletMaxWidth;

    load = {
      width: side,
      length: side,
      isVisible,
    };
  }
  return load;
};

const calculate8D = (dimensions: Dimensions, defaultVisibility: boolean = true) => {
  // The side is twice the smallest side + longest side of the package.
  const side = (2 * dimensions.width) + dimensions.length;
  const isVisible = side <= dimensions.palletMaxLength && side <= dimensions.palletMaxWidth;

  const load: LoadDimensions = {
    width: side,
    length: side,
    isVisible: defaultVisibility && isVisible,
  };
  return load;
};

const calculate10J = (dimensions: Dimensions, overlap: OverlapValues) => {
  const { width = 0, length = 0 } = dimensions;
  const { horizontal = 0, vertical = 0 } = overlap;

  const totalWidthNoSpace = 4 * width;
  const totalWidthWithSpace = 2 * length - horizontal;
  const totalLength = ((2 * length) + width) - vertical + width;
  let totalWidth = totalWidthNoSpace;
  if (totalWidthNoSpace > totalWidthWithSpace) {
    totalWidth = totalWidthNoSpace;
  }

  const isVisible = loadFitInPallet(totalWidth, totalLength, dimensions);

  const load: LoadDimensions = {
    width: totalWidth,
    length: totalLength,
    isVisible,
  };
  return load;
};

const calculate6B = (dimensions: Dimensions, overlap: OverlapValues) => {
  const totalWidthA = (2 * dimensions.width) + dimensions.length - overlap.horizontal;
  const totalWidthB = 2 * dimensions.length - overlap.horizontal;
  const totalLengthA = dimensions.length + dimensions.width - overlap.vertical;
  const totalLengthB = 3 * dimensions.width - overlap.vertical;
  const totalWidth = totalWidthA > totalWidthB ? totalWidthA : totalWidthB;
  const totalLength = totalLengthA > totalLengthB ? totalLengthA : totalLengthB;
  const isVisible = loadFitInPallet(totalWidth, totalLength, dimensions);

  const load: LoadDimensions = {
    width: totalWidth,
    length: totalLength,
    isVisible,
  };
  return load;
};

const calculate6D = (dimensions: Dimensions, overlap: OverlapValues) => {
  const totalWidthA = 4 * dimensions.width;
  const totalWidthB = 2 * dimensions.length - overlap.horizontal;
  const totalWidth = totalWidthA > totalWidthB ? totalWidthA : totalWidthB;
  const totalLength = dimensions.length + dimensions.width - overlap.vertical;
  const isVisible = loadFitInPallet(totalWidth, totalLength, dimensions);

  const load: LoadDimensions = {
    width: totalWidth,
    length: totalLength,
    isVisible,
  };
  return load;
};

const calculate12B = (dimensions: Dimensions, overlap: OverlapValues) => {
  const totalWidthA = dimensions.length + (3 * dimensions.width) - overlap.horizontal;
  const totalWidthB = 3 * dimensions.length - overlap.horizontal;
  const totalLengthA = 4 * dimensions.width;
  const totalLengthB = (2 * dimensions.length) + dimensions.width - overlap.vertical;

  const totalWidth = totalWidthA > totalWidthB ? totalWidthA : totalWidthB;
  const totalLength = totalLengthA > totalLengthB ? totalLengthA : totalLengthB;
  const isVisible = loadFitInPallet(totalWidth, totalLength, dimensions);

  const load: LoadDimensions = {
    width: totalWidth,
    length: totalLength,
    isVisible,
  };
  return load;
};

export const triagePattern = (
  label: string,
  dimensions: Dimensions,
  overlapAllowed: boolean = false,
): LoadDimensions => {
  let grid1;
  let grid2;
  let defaultVisibility = true;

  const overlap: OverlapValues = {
    horizontal: overlapAllowed ? dimensions.horizontalOverlap : 0,
    vertical: overlapAllowed ? dimensions.verticalOverlap : 0,
    allowed: overlapAllowed,
  };

  switch (label) {
    // Composed Grid cases.
    case PatternId.A3:
      grid1 = { cols: 1, rows: 2 };
      grid2 = { cols: 1, rows: 1 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A5:
      grid1 = { cols: 1, rows: 3 };
      grid2 = { cols: 1, rows: 2 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A7:
      grid1 = { cols: 1, rows: 3 };
      grid2 = { cols: 2, rows: 2 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.C8:
      grid1 = { cols: 2, rows: 3 };
      grid2 = { cols: 1, rows: 2 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.B9:
      grid1 = { cols: 1, rows: 3 };
      grid2 = { cols: 3, rows: 2 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.D9:
      grid1 = { cols: 1, rows: 5 };
      grid2 = { cols: 2, rows: 2 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.C10:
      grid1 = { cols: 1, rows: 4 };
      grid2 = { cols: 2, rows: 3 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A11:
      grid1 = { cols: 2, rows: 4 };
      grid2 = { cols: 1, rows: 3 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A13:
      grid1 = { cols: 1, rows: 4 };
      grid2 = { cols: 3, rows: 3 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.B13:
      grid1 = { cols: 2, rows: 5 };
      grid2 = { cols: 1, rows: 3 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A14:
      grid1 = { cols: 2, rows: 4 };
      grid2 = { cols: 2, rows: 3 };
      return calculateComposedGrid(grid1, grid2, dimensions, overlap);
    case PatternId.A15:
      grid1 = { cols: 3, rows: 4 };
      grid2 = { cols: 1, rows: 3 };
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateComposedGrid(grid1, grid2, dimensions, overlap, defaultVisibility);
    case PatternId.A18:
      grid1 = { cols: 2, rows: 5 };
      grid2 = { cols: 2, rows: 4 };
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateComposedGrid(grid1, grid2, dimensions, overlap, defaultVisibility);
    // Grid cases.
    case PatternId.B4:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(2, 2, dimensions, defaultVisibility);
    case PatternId.A6:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(2, 3, dimensions, defaultVisibility);
    case PatternId.A8:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(2, 4, dimensions, defaultVisibility);
    case PatternId.A9:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(3, 3, dimensions, defaultVisibility);
    case PatternId.A10:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(2, 5, dimensions, defaultVisibility);
    case PatternId.A12:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(4, 3, dimensions, defaultVisibility);
    case PatternId.D16:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(4, 4, dimensions, defaultVisibility);
    case PatternId.C20:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGrid(4, 5, dimensions, defaultVisibility);
    // With spaces.
    case PatternId.A4:
      return calculate4A(dimensions, overlap);
    case PatternId.D8:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculate8D(dimensions, defaultVisibility);
    case PatternId.J10:
      return calculate10J(dimensions, overlap);
    // Complex.
    case PatternId.B6:
      return calculate6B(dimensions, overlap);
    case PatternId.D6:
      return calculate6D(dimensions, overlap);
    case PatternId.B12:
      return calculate12B(dimensions, overlap);
    case PatternId.GRID:
      // Even if overlap is generally allowed, this pattern does not support overlap.
      defaultVisibility = !overlapAllowed;
      return calculateGridSize(dimensions, defaultVisibility);
    default:
      return { isVisible: false };
  }
};

export const getDimensions = (options: CalculatorOptions) => {
  const {
    alignmentType,
    palletLength,
    palletWidth,
    packageLength,
    packageWidth,
    packageDimensionType,
    palletUnitType,
    horizontalOverlap,
    verticalOverlap,
  } = options;

  const maxOverhang = palletUnitType === UnitDimension.INCH ? MAX_OVERHANG_INCHES : MAX_OVERHANG_CM;
  const overhang = alignmentType === LayerPatternType.ALIGNED ? 0 : maxOverhang;

  const palletMaxWidth = 2 * overhang + Number(palletWidth);
  const palletMaxLength = 2 * overhang + Number(palletLength);

  let width = Number(packageWidth);
  let length = Number(packageLength);

  // If units are different, convert package dimensions to pallet unit.
  if (packageDimensionType !== palletUnitType) {
    width = convertTo(Number(packageWidth), palletUnitType);
    length = convertTo(Number(packageLength), palletUnitType);
  }

  // To calculate visibility, we need to know what is the biggest side of
  // the package, assuming it is length, if it isn't we switch values.
  if (length < width) {
    const temp = width;
    width = length;
    length = temp;
  }
  const dimensions: Dimensions = {
    width,
    length,
    palletMaxWidth,
    palletMaxLength,
    widthOverhang: overhang,
    lengthOverhang: overhang,
    horizontalOverlap,
    verticalOverlap,
  };
  return dimensions;
};

export const shouldRotateLoad = (pattern: LayerPatternItem, options: CalculatorOptions) => {
  const {
    label,
  } = pattern;

  const dimensions = getDimensions(options);
  const patternDimensions = triagePattern(label, dimensions);

  const {
    width: loadWidth,
    length: loadLength,
  } = patternDimensions;

  const {
    palletWidth,
    palletLength,
  } = options;

  // Normal, just take lowest between pallet and load width/width and length/length.
  let x = Number(palletWidth) < Number(loadWidth) ? palletWidth : loadWidth;
  let y = Number(palletLength) < Number(loadLength) ? palletLength : loadLength;
  const normalArea = Number(x) * Number(y);

  // Inverted, take lowest between pallet width/ load length and pallet length/ load width.
  x = Number(palletWidth) < Number(loadLength) ? palletWidth : loadLength;
  y = Number(palletLength) < Number(loadWidth) ? palletLength : loadWidth;
  const rotatedArea = Number(x) * Number(y);
  return rotatedArea > normalArea;
};

export const patternIsVisible = (
  pattern: LayerPatternItem,
  options: CalculatorOptions,
  overlapAllowed: boolean,
) => {
  const {
    value,
    label,
  } = pattern;

  const {
    unitsPerLayer,
  } = options;

  const temporalDimensions = getDimensions(options);
  let dimensions;

  if (!isNil(unitsPerLayer)) {
    if (Number(value) === unitsPerLayer) {
      dimensions = triagePattern(label, temporalDimensions, overlapAllowed);
      return dimensions.isVisible;
    }
    return false;
  }
  dimensions = triagePattern(label, temporalDimensions, overlapAllowed);

  return dimensions.isVisible;
};

export const getPatternDimensions = (
  pattern: LayerPatternItem,
  options: CalculatorOptions,
  overlapAllowed = false,
) => {
  const {
    label,
  } = pattern;

  const dimensions = getDimensions(options);
  return triagePattern(label, dimensions, overlapAllowed);
};
